import {
  createSignal,
  Show,
  onMount,
  onCleanup,
  For,
  JSX,
  createEffect,
  on,
} from 'solid-js';
import { Portal } from 'solid-js/web';
import { Icon } from '@tcweb/connect-ui';
import { ColorPicker } from '@trimble/ui-components/solidjs';
import { RgbColor } from 'solid-color';
import { getRGBColorString } from '@trimble/utilities';
import {
  DEFAULT_POLYGON_MEASUREMENT_SETTINGS,
  LineStyle,
  PointSymbol,
} from '@trimble/web3d-providers';
import { t } from '@trimble/i18n';
import { LineStyleDropdown } from './LineStyleDropdown';
import { LineThicknessDropdown } from './LineThicknessDropdown';
import { useViewportAnchoredPosition } from './useViewportAnchoredPosition';
import { DEFAULT_COLOR_PICKER_ESTIMATED_SIZE } from './viewport-anchored-position';

interface MeasurementStyleProps {
  class?: string;
  color: RgbColor;
  onColorChange(color: RgbColor): void;
  selectedPointSymbol?: PointSymbol;
  pointSymbols?: PointSymbol[];
  onPointSymbolChange?(symbol: PointSymbol | undefined): void;
  disabled?: boolean;
  disabledReason?: string;
  fillOpacity?: number;
  lineStyle?: LineStyle;
  lineThickness?: number;
  onFillOpacityChange?(value: number): void;
  onLineThicknessChange?(value: number): void;
  onLineStyleChange?(value: LineStyle): void;
}

export const MeasurementStyle = (props: MeasurementStyleProps): JSX.Element => {
  const [showColorPicker, setShowColorPicker] = createSignal(false);
  const defaultColor: RgbColor = { r: 236, g: 0, b: 182 }; // Default color #EC00B6
  const [editedColor, setEditedColor] = createSignal<RgbColor>(defaultColor);
  const {
    position,
    setAnchorElement,
    setFloatingElement,
    schedulePositionUpdate,
  } = useViewportAnchoredPosition({
    isOpen: showColorPicker,
    estimatedFloatingSize: DEFAULT_COLOR_PICKER_ESTIMATED_SIZE,
  });

  let colorBtnRef: HTMLButtonElement | undefined;
  let colorPickerPortalRef: HTMLDivElement | undefined;

  const handleColorSave = (color: RgbColor): void => {
    setEditedColor(color);
    props.onColorChange(color);
  };

  const handlePointSymbolClick = (symbol: PointSymbol): void => {
    if (props.selectedPointSymbol?.name === symbol.name) {
      props.onPointSymbolChange?.(undefined);
    } else {
      props.onPointSymbolChange?.(symbol);
    }
  };

  const handleFillOpacityChange = (event: CustomEvent): void => {
    const value = Number.parseFloat(
      (event.detail?.target as HTMLInputElement)?.value ?? ''
    );
    if (Number.isNaN(value)) {
      return;
    }
    props.onFillOpacityChange?.(value);
  };

  const handleLineThicknessChange = (value: number): void => {
    props.onLineThicknessChange?.(value);
  };

  const handleLineStyleChange = (value: LineStyle): void => {
    props.onLineStyleChange?.(value);
  };

  function toggleColorPicker(): void {
    const willOpen = !showColorPicker();
    setShowColorPicker(willOpen);
    if (willOpen) {
      schedulePositionUpdate();
    }
  }

  const handleClickOutside = (e: MouseEvent | TouchEvent): void => {
    if (!showColorPicker()) return;
    const target = e.target as HTMLElement;
    if (
      (colorBtnRef && colorBtnRef.contains(target)) ||
      (colorPickerPortalRef && colorPickerPortalRef.contains(target))
    ) {
      return;
    }
    setShowColorPicker(false);
  };

  onMount(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
  });

  onCleanup(() => {
    document.removeEventListener('mousedown', handleClickOutside);
    document.removeEventListener('touchstart', handleClickOutside);
  });

  createEffect(
    on(
      () => props.color,
      (color: RgbColor) => {
        setEditedColor(color);
      }
    )
  );

  return (
    <div class={props.class ?? 'flex flex-col gap-4 p-3'}>
      {/* Color Section */}
      <div>
        <div class="text-sm text-gray-8 mb-2">{t('general.color')}</div>
        <modus-wc-tooltip
          content={props.disabledReason}
          position="bottom"
          disabled={!props.disabled}
        >
          <button
            ref={(el) => {
              colorBtnRef = el;
              setAnchorElement(el);
            }}
            class={`w-6 h-6 rounded-full ${props.disabled ? 'opacity-50' : ''}`}
            style={{
              ['background-color']: getRGBColorString(editedColor()),
              cursor: props.disabled ? 'not-allowed' : 'pointer',
            }}
            onClick={toggleColorPicker}
            disabled={props.disabled}
            data-testid="measurement-style-color-button"
          />
        </modus-wc-tooltip>
        <Show when={showColorPicker()}>
          <Portal>
            <div
              ref={(el) => {
                colorPickerPortalRef = el;
                setFloatingElement(el);
              }}
              class="fixed z-dropdown"
              style={{
                left: `${String(position().left)}px`,
                top: `${String(position().top)}px`,
              }}
              data-testid="measurement-style-color-picker"
            >
              <ColorPicker
                cssAlignment=""
                initialColor={getRGBColorString(editedColor())}
                onSave={handleColorSave}
              />
            </div>
          </Portal>
        </Show>
      </div>

      <Show when={props.fillOpacity !== undefined}>
        <div>
          <div class="text-sm text-gray-8 mb-2">
            {t('measurements.measurementStyle.transparency')}
          </div>
          <modus-wc-tooltip
            content={props.disabledReason}
            position="bottom"
            disabled={!props.disabled}
          >
            <modus-wc-slider
              size="sm"
              data-testid="measurement-fill-opacity-slider"
              class="h-5"
              attr:min={0.01}
              attr:max={1}
              attr:step={0.01}
              attr:value={props.fillOpacity}
              attr:disabled={props.disabled}
              on:inputChange={handleFillOpacityChange}
            />
          </modus-wc-tooltip>
        </div>
      </Show>

      <Show
        when={
          props.lineThickness !== undefined || props.lineStyle !== undefined
        }
      >
        <div>
          <div class="text-sm text-gray-8 mb-2">
            {t('measurements.measurementStyle.stroke')}
          </div>
          <div class="grid grid-cols-2 gap-2">
            <div class="flex flex-col gap-1">
              <div class="text-sm text-gray-6">
                {t('measurements.measurementStyle.weight')}
              </div>
              <LineThicknessDropdown
                value={
                  props.lineThickness !== undefined
                    ? props.lineThickness
                    : DEFAULT_POLYGON_MEASUREMENT_SETTINGS.lineThickness
                }
                onValueChange={handleLineThicknessChange}
                disabled={props.disabled}
                disabledReason={props.disabledReason}
              />
            </div>

            <div class="flex flex-col gap-1">
              <div class="text-sm text-gray-6">{t('measurements.style')}</div>
              <LineStyleDropdown
                value={
                  props.lineStyle !== undefined
                    ? props.lineStyle
                    : DEFAULT_POLYGON_MEASUREMENT_SETTINGS.lineStyle
                }
                onValueChange={handleLineStyleChange}
                disabled={props.disabled}
                disabledReason={props.disabledReason}
              />
            </div>
          </div>
        </div>
      </Show>

      {/* Symbol Section */}
      <Show when={props.pointSymbols && props.pointSymbols.length > 0}>
        <div>
          <div class="text-sm text-gray-8 mb-2">Symbol</div>
          <div class="flex gap-1 flex-wrap">
            <For each={props.pointSymbols}>
              {(pointSymbol) => {
                const isSelected = (): boolean =>
                  props.selectedPointSymbol?.name === pointSymbol.name;
                return (
                  <modus-wc-tooltip
                    content={props.disabledReason}
                    position="bottom"
                    disabled={!props.disabled}
                  >
                    <div
                      class={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
                        props.disabled
                          ? 'cursor-not-allowed opacity-50'
                          : 'cursor-pointer bg-gray-light hover:bg-gray-0'
                      } ${isSelected() ? 'border-2 border-gray-600' : ''}`}
                      onClick={() => {
                        if (!props.disabled) {
                          handlePointSymbolClick(pointSymbol);
                        }
                      }}
                    >
                      <span data-testid="symbol-icon">
                        <Icon
                          icon={pointSymbol.name}
                          url={pointSymbol.url}
                          size="icon-medium"
                        />
                      </span>
                    </div>
                  </modus-wc-tooltip>
                );
              }}
            </For>
          </div>
        </div>
      </Show>
    </div>
  );
};
